(() => {
  'use strict';

  const state = {
    panel: null,
    trigger: null,
    scrollY: 0,
    cartBusy: false,
  };

  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => {
      const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
      return entities[character];
    });

  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const getPanel = (name) => document.querySelector(`[data-lv-mobile-panel="${name}"]`);

  const lockPage = () => {
    state.scrollY = window.scrollY;
    document.body.classList.add('lv-mobile-scroll-locked');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${state.scrollY}px`;
    document.body.style.width = '100%';
  };

  const unlockPage = () => {
    document.body.classList.remove('lv-mobile-scroll-locked');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, state.scrollY);
  };

  const openPanel = (name, trigger) => {
    const panel = getPanel(name);
    if (!panel) return;
    if (state.panel && state.panel !== panel) closePanel(false);

    state.panel = panel;
    state.trigger = trigger || document.activeElement;
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    document.querySelectorAll(`[data-lv-mobile-panel-open="${name}"]`).forEach((button) => {
      button.setAttribute('aria-expanded', 'true');
    });
    lockPage();
    window.requestAnimationFrame(() => {
      panel.querySelector(focusableSelector)?.focus();
    });
  };

  const closePanel = (restoreFocus = true) => {
    if (!state.panel) return;
    const name = state.panel.dataset.lvMobilePanel;
    state.panel.classList.remove('is-open');
    state.panel.setAttribute('aria-hidden', 'true');
    document.querySelectorAll(`[data-lv-mobile-panel-open="${name}"]`).forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
    unlockPage();
    const returnTarget = state.trigger;
    state.panel = null;
    state.trigger = null;
    if (restoreFocus && returnTarget instanceof HTMLElement) returnTarget.focus();
  };

  const trapFocus = (event) => {
    if (!state.panel || event.key !== 'Tab') return;
    const focusable = [...state.panel.querySelectorAll(focusableSelector)].filter(
      (element) => element.offsetParent !== null
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const renderCart = (cart) => {
    const drawer = getPanel('cart');
    if (!drawer) return;
    const currency = drawer.dataset.moneyCode || cart.currency || 'USD';
    const lines = drawer.querySelector('[data-lv-mobile-cart-lines]');
    if (!lines) return;

    if (!cart.items.length) {
      const shopAllUrl = escapeHtml(drawer.dataset.shopAllUrl || '/');
      lines.innerHTML = `
        <div class="lv-mobile-cart-drawer__empty">
          <h3>Your cart is empty</h3>
          <p>Explore LVSEFOD products and add your favorites.</p>
          <a class="lv-mobile-button" href="${shopAllUrl}">Shop all</a>
        </div>`;
    } else {
      lines.innerHTML = cart.items
        .map((item, index) => {
          const image = item.image
            ? `<img class="lv-mobile-cart-line__image" src="${escapeHtml(
                `${item.image}${item.image.includes('?') ? '&' : '?'}width=220`
              )}" alt="${escapeHtml(item.product_title)}" loading="lazy" width="110" height="110">`
            : '<svg class="lv-mobile-cart-line__placeholder" viewBox="0 0 100 100" aria-hidden="true"><rect width="100" height="100" fill="#eef0ed"/><path d="M25 70 45 45l12 13 9-10 14 22Z" fill="#c9cec7"/></svg>';
          const variant =
            item.variant_title && item.variant_title !== 'Default Title'
              ? `<p>${escapeHtml(item.variant_title)}</p>`
              : '';
          return `
            <article class="lv-mobile-cart-line" data-line="${index + 1}">
              <a class="lv-mobile-cart-line__media" href="${escapeHtml(item.url)}">${image}</a>
              <div class="lv-mobile-cart-line__details">
                <a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
                ${variant}
                <div class="lv-mobile-cart-line__actions">
                  <div class="lv-mobile-quantity">
                    <button type="button" data-lv-mobile-cart-change="${index + 1}" data-quantity="${Math.max(
                      0,
                      item.quantity - 1
                    )}" aria-label="Decrease ${escapeHtml(item.product_title)} quantity">−</button>
                    <span aria-label="Quantity">${item.quantity}</span>
                    <button type="button" data-lv-mobile-cart-change="${index + 1}" data-quantity="${
                      item.quantity + 1
                    }" aria-label="Increase ${escapeHtml(item.product_title)} quantity">+</button>
                  </div>
                  <button class="lv-mobile-cart-line__remove" type="button" data-lv-mobile-cart-change="${
                    index + 1
                  }" data-quantity="0">Remove</button>
                  <strong>${money(item.final_line_price, currency)}</strong>
                </div>
              </div>
            </article>`;
        })
        .join('');
    }

    document.querySelectorAll('[data-lv-mobile-cart-count]').forEach((element) => {
      element.textContent = cart.item_count;
    });
    drawer.querySelector('[data-lv-mobile-cart-subtotal]').textContent = money(cart.total_price, currency);

    const gift = drawer.querySelector('[data-lv-mobile-gift-threshold]');
    if (gift) {
      const threshold = Number(gift.dataset.lvMobileGiftThreshold || 0);
      const progress = threshold > 0 ? Math.min(100, (cart.total_price / threshold) * 100) : 0;
      gift.style.setProperty('--lv-mobile-gift-progress', `${progress}%`);
    }
  };

  const showCartError = (message) => {
    const error = getPanel('cart')?.querySelector('[data-lv-mobile-cart-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  };

  const refreshCart = async () => {
    const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Cart could not be refreshed.');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const changeCartLine = async (line, quantity) => {
    if (state.cartBusy) return;
    const drawer = getPanel('cart');
    state.cartBusy = true;
    drawer?.classList.add('is-loading');
    showCartError('');
    try {
      const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Cart update failed.');
      renderCart(result);
    } catch (error) {
      showCartError(error.message || 'Cart update failed. Please try again.');
    } finally {
      state.cartBusy = false;
      drawer?.classList.remove('is-loading');
    }
  };

  const syncVariant = (root) => {
    const variantSelect = root.querySelector('[data-lv-mobile-variant-select]');
    if (!variantSelect) return;
    const selectedOptions = [...root.querySelectorAll('[data-lv-mobile-option]')].map((select) => select.value);
    let option = variantSelect.selectedOptions[0];
    if (selectedOptions.length) {
      option = [...variantSelect.options].find((variantOption) =>
        selectedOptions.every((value, index) => variantOption.dataset[`option${index}`] === value)
      );
    }

    const price = root.querySelector('[data-lv-mobile-product-price]');
    const compare = root.querySelector('[data-lv-mobile-product-compare]');
    const addButton = root.querySelector('[data-lv-mobile-add-to-cart]');
    const addLabel = root.querySelector('[data-lv-mobile-add-label]');
    const stickyButton = root.querySelector('[data-lv-mobile-sticky-add]');
    const stickyLabel = root.querySelector('[data-lv-mobile-sticky-label]');
    const stickyPrice = root.querySelector('[data-lv-mobile-sticky-price]');
    const available = option?.dataset.available === 'true';

    if (!option) {
      variantSelect.value = '';
      addButton.disabled = true;
      stickyButton.disabled = true;
      addLabel.textContent = 'Unavailable';
      stickyLabel.textContent = 'Unavailable';
      return;
    }

    variantSelect.value = option.value;
    price.textContent = option.dataset.price;
    stickyPrice.textContent = option.dataset.price;
    const compareVisible = Number(option.dataset.compareCents || 0) > Number(option.dataset.priceCents || 0);
    compare.textContent = option.dataset.compare || '';
    compare.hidden = !compareVisible;
    addButton.disabled = !available;
    stickyButton.disabled = !available;
    addLabel.textContent = available ? 'Add to cart' : 'Sold out';
    stickyLabel.textContent = available ? 'Add to cart' : 'Sold out';

    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    window.history.replaceState({}, '', url);
  };

  const submitProduct = async (form) => {
    const root = form.closest('[data-lv-mobile-product]');
    const button = form.querySelector('[data-lv-mobile-add-to-cart]');
    const status = form.querySelector('[data-lv-mobile-product-status]');
    if (button.disabled || form.dataset.lvMobileSubmitting === 'true') return;
    form.dataset.lvMobileSubmitting = 'true';
    button.disabled = true;
    status.hidden = true;

    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'This product could not be added.');
      await refreshCart();
      openPanel('cart', button);
    } catch (error) {
      status.textContent = error.message || 'This product could not be added. Please try again.';
      status.hidden = false;
    } finally {
      form.dataset.lvMobileSubmitting = 'false';
      syncVariant(root);
    }
  };

  const initSlider = (slider) => {
    if (slider.dataset.lvMobileReady === 'true') return;
    slider.dataset.lvMobileReady = 'true';
    const track = slider.querySelector('[data-lv-mobile-slider-track]');
    const dots = [...slider.querySelectorAll('[data-lv-mobile-slide-to]')];
    if (!track || !dots.length) return;

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const slide = track.children[Number(dot.dataset.lvMobileSlideTo)];
        slide?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      });
    });

    let frame;
    track.addEventListener(
      'scroll',
      () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
          dots.forEach((dot, dotIndex) => {
            if (dotIndex === index) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
        });
      },
      { passive: true }
    );
  };

  const initGallery = (root) => {
    const gallery = root.querySelector('[data-lv-mobile-product-gallery]');
    const track = gallery?.querySelector('.lv-mobile-product-gallery__track');
    const thumbs = [...(gallery?.querySelectorAll('[data-lv-mobile-gallery-thumb]') || [])];
    const current = gallery?.querySelector('[data-lv-mobile-gallery-current]');
    if (!track || !thumbs.length) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        track.children[Number(thumb.dataset.lvMobileGalleryThumb)]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });
      });
    });

    let frame;
    track.addEventListener(
      'scroll',
      () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
          current.textContent = index + 1;
          thumbs.forEach((thumb, thumbIndex) => {
            if (thumbIndex === index) thumb.setAttribute('aria-current', 'true');
            else thumb.removeAttribute('aria-current');
          });
        });
      },
      { passive: true }
    );
  };

  const initStickyProduct = (root) => {
    const sticky = root.querySelector('[data-lv-mobile-product-sticky]');
    const buyRow = root.querySelector('.lv-mobile-product-form__buy-row');
    if (!sticky || !buyRow || !('IntersectionObserver' in window)) return;
    let buyVisible = true;
    let footerVisible = false;
    const update = () => sticky.classList.toggle('is-visible', !buyVisible && !footerVisible);
    new IntersectionObserver((entries) => {
      buyVisible = entries[0].isIntersecting;
      update();
    }).observe(buyRow);
    const footer = document.querySelector('.lv-mobile-footer');
    if (footer) {
      new IntersectionObserver((entries) => {
        footerVisible = entries[0].isIntersecting;
        update();
      }).observe(footer);
    }
  };

  const initProduct = (root) => {
    if (root.dataset.lvMobileReady === 'true') return;
    root.dataset.lvMobileReady = 'true';
    syncVariant(root);
    initGallery(root);
    initStickyProduct(root);
  };

  const initAccordions = (scope) => {
    scope.querySelectorAll('details').forEach((details) => {
      if (details.dataset.lvMobileA11y === 'true') return;
      details.dataset.lvMobileA11y = 'true';
      const summary = details.querySelector(':scope > summary');
      if (!summary) return;
      summary.setAttribute('aria-expanded', String(details.open));
      details.addEventListener('toggle', () => summary.setAttribute('aria-expanded', String(details.open)));
    });
  };

  const initSections = (scope = document) => {
    scope.querySelectorAll('[data-lv-mobile-slider]').forEach(initSlider);
    scope.querySelectorAll('[data-lv-mobile-product]').forEach(initProduct);
    initAccordions(scope);
  };

  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-lv-mobile-panel-open]');
    if (open) {
      event.preventDefault();
      const name = open.dataset.lvMobilePanelOpen;
      if (name === 'cart') refreshCart().catch(() => {});
      openPanel(name, open);
      return;
    }

    const close = event.target.closest('[data-lv-mobile-panel-close]');
    if (close) {
      event.preventDefault();
      closePanel();
      return;
    }

    const cartChange = event.target.closest('[data-lv-mobile-cart-change]');
    if (cartChange) {
      event.preventDefault();
      changeCartLine(cartChange.dataset.lvMobileCartChange, cartChange.dataset.quantity);
      return;
    }

    const quantityChange = event.target.closest('[data-lv-mobile-quantity-change]');
    if (quantityChange) {
      const input = quantityChange.closest('form')?.querySelector('[data-lv-mobile-product-quantity]');
      if (!input) return;
      const step = Number(input.step || 1);
      const min = Number(input.min || 1);
      input.value = Math.max(min, Number(input.value || min) + Number(quantityChange.dataset.lvMobileQuantityChange) * step);
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const stickyAdd = event.target.closest('[data-lv-mobile-sticky-add]');
    if (stickyAdd) {
      stickyAdd.closest('[data-lv-mobile-product]')?.querySelector('[data-lv-mobile-product-form]')?.requestSubmit();
      return;
    }

    const gridView = event.target.closest('[data-lv-mobile-grid-view]');
    if (gridView) {
      const collection = gridView.closest('[data-lv-mobile-collection]');
      collection?.querySelector('[data-lv-mobile-collection-grid]')?.classList.toggle('is-list', gridView.dataset.lvMobileGridView === 'list');
      collection?.querySelectorAll('[data-lv-mobile-grid-view]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button === gridView));
      });
      return;
    }

    const announcementClose = event.target.closest('[data-lv-mobile-announcement-close]');
    if (announcementClose) {
      announcementClose.closest('[data-lv-mobile-announcement]').hidden = true;
      return;
    }

    const share = event.target.closest('[data-lv-mobile-share]');
    if (share) {
      const url = share.dataset.shareUrl || window.location.href;
      if (navigator.share) navigator.share({ title: document.title, url }).catch(() => {});
      else navigator.clipboard?.writeText(url).then(() => {
        share.textContent = 'Link copied';
      });
    }
  });

  document.addEventListener('change', (event) => {
    const option = event.target.closest('[data-lv-mobile-option]');
    if (option) syncVariant(option.closest('[data-lv-mobile-product]'));

    const purchase = event.target.closest('input[name="lv-mobile-purchase-option"]');
    if (purchase) {
      const plan = purchase.closest('form')?.querySelector('[data-lv-mobile-selling-plan]');
      if (plan) plan.disabled = purchase.value !== 'subscription';
    }

    const sort = event.target.closest('[data-lv-mobile-sort]');
    if (sort) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-lv-mobile-product-form]');
    if (!form) return;
    event.preventDefault();
    submitProduct(form);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && state.panel) {
      event.preventDefault();
      closePanel();
      return;
    }
    trapFocus(event);
  });

  document.addEventListener('shopify:section:load', (event) => initSections(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSections());
  } else {
    initSections();
  }
})();
